document.addEventListener('DOMContentLoaded', () => {
  if (window.__communityCtaInit) return;
  window.__communityCtaInit = true;

  const MODAL = '.community-cta__modal';
  const HTML = document.documentElement;
  const swipers = new WeakMap();

  const ensureSwiper = (modal) => {
    if (swipers.has(modal)) return swipers.get(modal);
    if (typeof Swiper === 'undefined') return null;

    const container = modal.querySelector('.community-cta__swiper');
    if (!container) return null;

    const swiper = new Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 0,
      preventClicks: false,
      preventClicksPropagation: false,
      pagination: {
        el: modal.querySelector('.community-cta__pagination'),
        clickable: true,
      },
      navigation: {
        prevEl: modal.querySelector('.community-cta__nav--prev'),
        nextEl: modal.querySelector('.community-cta__nav--next'),
      },
      on: {
        slideChange: () => {
          setActivePanel(modal, swiper.activeIndex);
          clearHotspots(modal);
        },
      },
    });

    swipers.set(modal, swiper);
    return swiper;
  };

  const setActivePanel = (modal, index) => {
    const panels = [...modal.querySelectorAll('[data-cc-panel]')];
    for (const panel of panels) {
      const isActive = panel.dataset.lookIndex === String(index);
      panel.classList.toggle('is-active', isActive);
      if (!isActive) closeDetails(panel);
    }
  };

  const closeDetails = (panel) => {
    panel.classList.remove('is-detail-open');
    for (const detail of panel.querySelectorAll('.community-cta__detail')) {
      detail.classList.remove('is-open');
    }
    panel.closest('.community-cta__modal-box')?.classList.remove('is-detail-open');
  };

  const clearHotspots = (modal) => {
    for (const hotspot of modal.querySelectorAll('.community-cta__hotspot.is-active')) {
      hotspot.classList.remove('is-active');
    }
  };

  const openModal = (modal, lookIndex) => {
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    HTML.classList.add('no-scroll');
    setActivePanel(modal, lookIndex);

    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      const swiper = ensureSwiper(modal);
      if (swiper) {
        swiper.update();
        swiper.slideTo(lookIndex, 0);
      }
    });
  };

  const closeModal = (modal) => {
    modal.classList.remove('is-open');
    modal.classList.remove('is-behind-cart');
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    HTML.classList.remove('no-scroll');
    clearHotspots(modal);
    const box = modal.querySelector('.community-cta__modal-box');
    box?.classList.remove('is-drawer-collapsed');
    for (const panel of modal.querySelectorAll('[data-cc-panel]')) closeDetails(panel);

    const card = modal.querySelector('[data-cc-hotspot-card]');
    if (card) card.classList.remove('is-visible');
    box?.style.removeProperty('--cc-sheet-h');
  };

  const watchCart = (modal, onOpen) => {
    const cart = document.getElementById('sidebar-cart');
    if (!cart) {
      onOpen?.();
      return;
    }

    let opened = false;
    const observer = new MutationObserver(() => {
      const isOpen = cart.getAttribute('aria-hidden') === 'false';
      if (isOpen && !opened) {
        opened = true;
        modal?.classList.add('is-behind-cart');
        onOpen?.();
      } else if (!isOpen && opened) {
        modal?.classList.remove('is-behind-cart');
        observer.disconnect();
      }
    });
    observer.observe(cart, { attributes: true, attributeFilter: ['aria-hidden'] });
  };

  const revealProductCard = (modal, lookIndex, productIndex) => {
    const panel = modal.querySelector(`[data-cc-panel][data-look-index="${lookIndex}"]`);
    if (!panel) return;

    modal.querySelector('.community-cta__modal-box')?.classList.remove('is-drawer-collapsed');

    for (const card of panel.querySelectorAll('[data-cc-open-product]')) {
      const isMatch = card.dataset.productIndex === String(productIndex);
      card.classList.toggle('is-highlight', isMatch);
      if (isMatch) card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    openDetail(modal, lookIndex, productIndex);
  };

  const showHotspotCard = (modal, hotspot) => {
    const card = modal.querySelector('[data-cc-hotspot-card]');
    const tooltip = hotspot.querySelector('.community-cta__tooltip');
    if (!card || !tooltip) return;

    const clone = tooltip.cloneNode(true);
    card.replaceChildren(...clone.childNodes);
    card.dataset.lookIndex = hotspot.dataset.lookIndex;
    card.dataset.productIndex = hotspot.dataset.productIndex;
    card.classList.add('is-visible');
  };

  const setupStickyVisibility = (detail) => {
    if (detail.dataset.ccStickyInit === 'true') return;
    const actions = detail.querySelector('.community-cta__actions');
    const sticky = detail.querySelector('.community-cta__sticky');
    if (!actions || !sticky) return;
    detail.dataset.ccStickyInit = 'true';

    const root = detail.closest('.community-cta__detail-col');
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          sticky.classList.toggle('is-visible', !entry.isIntersecting);
        }
      },
      { root: root || null, threshold: 0 }
    );
    observer.observe(actions);
  };

  const openDetail = (modal, lookIndex, productIndex) => {
    const panel = modal.querySelector(`[data-cc-panel][data-look-index="${lookIndex}"]`);
    if (!panel) return;

    panel.classList.add('is-detail-open');
    modal.querySelector('.community-cta__modal-box')?.classList.add('is-detail-open');
    for (const detail of panel.querySelectorAll('.community-cta__detail')) {
      const isTarget = detail.dataset.productIndex === String(productIndex);
      detail.classList.toggle('is-open', isTarget);
      if (isTarget) {
        loadRelated(detail);
        detail.scrollTop = 0;
        modal.querySelector('.community-cta__drawer')?.scrollTo({ top: 0 });
        setupStickyVisibility(detail);
        const descWrap = detail.querySelector('[data-cc-desc]');
        if (descWrap) {
          descWrap.classList.remove('is-expanded');
          const body = descWrap.querySelector('.community-cta__desc');
          requestAnimationFrame(() => {
            descWrap.classList.toggle('is-short', body.scrollHeight <= body.clientHeight + 2);
          });
        }
      }
    }
  };

  const loadRelated = async (detail) => {
    const related = detail.querySelector('[data-cc-related]');
    if (!related || related.dataset.loaded === 'true') return;
    related.dataset.loaded = 'true';

    const { productId, sectionId, loopShop } = related.dataset;
    const grid = related.querySelector('[data-cc-related-grid]');
    if (!productId || !grid) return;

    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    const limit = isMobile ? 6 : 9;

    const nativeFallback = async () => {
      if (!sectionId) return;
      try {
        const res = await fetch(
          `/recommendations/products?section_id=${sectionId}&product_id=${productId}&intent=related&limit=10`
        );
        if (!res.ok) throw new Error(`status ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const cards = [...doc.querySelectorAll('.community-cta__prod')];
        if (!cards.length) return;
        for (const card of cards) grid.appendChild(card);
        related.classList.add('is-visible');
      } catch (error) {
        console.error('[community-cta] native related fallback failed', error);
      }
    };

    try {
      const shop = loopShop || 'the-darkroast.myshopify.com';
      const loopRes = await fetch(`https://recommendationsapi.loopclub.io/v2/headless/query/?shop=${shop}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          queries: [
            {
              id: 'query1',
              max_recommendations: 12,
              product_ids: [Number.parseInt(productId, 10)],
              recommendation_type: 'similar_products',
            },
          ],
        }),
      });
      if (!loopRes.ok) throw new Error(`loop status ${loopRes.status}`);

      const data = await loopRes.json();
      const ids = (data?.query1?.products || []).map((p) => p.product_id).filter(Boolean).slice(0, limit);
      if (!ids.length) {
        await nativeFallback();
        return;
      }

      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const searchRes = await fetch(
        `/search?view=cc-related&type=product&limit=${limit}&q=${encodeURIComponent(query)}`
      );
      if (!searchRes.ok) throw new Error(`search status ${searchRes.status}`);

      const doc = new DOMParser().parseFromString(await searchRes.text(), 'text/html');
      const inner = doc.querySelector('.community-cta__related-cards');
      if (inner && inner.innerHTML.trim()) {
        grid.innerHTML = inner.innerHTML;
        related.classList.add('is-visible');
      } else {
        await nativeFallback();
      }
    } catch (error) {
      console.error('[community-cta] loop related failed', error);
      await nativeFallback();
    }
  };

  const selectSize = (button) => {
    if (button.disabled) return;
    const detail = button.closest('.community-cta__detail');
    if (!detail) return;

    for (const size of detail.querySelectorAll('[data-cc-size]')) {
      size.classList.remove('is-selected');
    }
    button.classList.add('is-selected');

    const { variantId, available } = button.dataset;
    const soldOut = available !== 'true';
    detail.classList.toggle('is-soldout', soldOut);

    for (const atc of detail.querySelectorAll('[data-cc-atc]')) {
      atc.dataset.variantId = variantId;
      atc.disabled = soldOut;
    }
  };

  const addToCart = async (button) => {
    if (button.disabled || button.dataset.ccLoading === 'true') return;
    const { variantId } = button.dataset;
    if (!variantId) return;

    button.dataset.ccLoading = 'true';
    button.classList.add('is-loading');
    const clearLoading = () => {
      delete button.dataset.ccLoading;
      button.classList.remove('is-loading');
    };

    const modal = button.closest(MODAL);
    watchCart(modal, clearLoading);
    const safety = setTimeout(clearLoading, 6000);

    try {
      if (typeof window.makerAddToCart === 'function') {
        window.makerAddToCart({ id: variantId }, false, true);
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (!response.ok) throw new Error(`status ${response.status}`);
        HTML.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      console.error('[community-cta] add to cart failed', error);
      clearTimeout(safety);
      clearLoading();
      modal?.classList.remove('is-behind-cart');
    }
  };

  const onClick = (event) => {
    const { target } = event;

    const tile = target.closest('[data-cc-open]');
    if (tile) {
      const modal = tile.closest('.community-cta')?.querySelector(MODAL);
      if (modal) openModal(modal, Number.parseInt(tile.dataset.lookIndex, 10) || 0);
      return;
    }

    if (target.closest('[data-cc-close]')) {
      target.closest(MODAL) && closeModal(target.closest(MODAL));
      return;
    }

    const drawerToggle = target.closest('[data-cc-drawer-toggle]');
    if (drawerToggle) {
      drawerToggle.closest('.community-cta__modal-box')?.classList.toggle('is-drawer-collapsed');
      return;
    }

    const hotspotCard = target.closest('[data-cc-hotspot-card]');
    if (hotspotCard) {
      const modal = hotspotCard.closest(MODAL);
      openDetail(modal, hotspotCard.dataset.lookIndex, hotspotCard.dataset.productIndex);
      return;
    }

    const hotspot = target.closest('[data-cc-hotspot]');
    if (hotspot) {
      const modal = hotspot.closest(MODAL);
      if (window.matchMedia('(max-width: 1024px)').matches) {
        showHotspotCard(modal, hotspot);
      } else {
        revealProductCard(modal, hotspot.dataset.lookIndex, hotspot.dataset.productIndex);
      }
      return;
    }

    const media = target.closest('.community-cta__media');
    if (media) {
      const openCard = media.querySelector('[data-cc-hotspot-card].is-visible');
      if (openCard) openCard.classList.remove('is-visible');
    }

    const card = target.closest('[data-cc-open-product]');
    if (card) {
      const modal = card.closest(MODAL);
      openDetail(modal, card.dataset.lookIndex, card.dataset.productIndex);
      return;
    }

    const back = target.closest('[data-cc-detail-back]');
    if (back) {
      const panel = back.closest('[data-cc-panel]');
      panel && closeDetails(panel);
      return;
    }

    const size = target.closest('[data-cc-size]');
    if (size) {
      selectSize(size);
      return;
    }

    const atc = target.closest('[data-cc-atc]');
    if (atc) {
      addToCart(atc);
      return;
    }

    const readmore = target.closest('[data-cc-readmore]');
    if (readmore) {
      const wrap = readmore.closest('[data-cc-desc]');
      const expanded = wrap?.classList.toggle('is-expanded');
      readmore.textContent = expanded ? 'Read less' : 'Read more';
      return;
    }
  };

  const onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const openModalEl = document.querySelector(`${MODAL}.is-open`);
    if (openModalEl) closeModal(openModalEl);
  };

  let sheetDrag = null;
  const onPointerDown = (event) => {
    const handle = event.target.closest('[data-cc-sheet-handle]');
    if (!handle) return;
    const drawer = handle.closest('.community-cta__drawer');
    const box = handle.closest('.community-cta__modal-box');
    if (!drawer || !box) return;
    sheetDrag = {
      box,
      startY: event.clientY,
      startH: drawer.getBoundingClientRect().height,
      minH: box.getBoundingClientRect().height * 0.18,
      maxH: box.getBoundingClientRect().height * 0.92,
    };
    handle.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event) => {
    if (!sheetDrag) return;
    const delta = sheetDrag.startY - event.clientY;
    const height = Math.min(sheetDrag.maxH, Math.max(sheetDrag.minH, sheetDrag.startH + delta));
    sheetDrag.box.style.setProperty('--cc-sheet-h', `${height}px`);
  };
  const onPointerUp = () => {
    sheetDrag = null;
  };

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('pointerdown', onPointerDown);
  document.addEventListener('pointermove', onPointerMove);
  document.addEventListener('pointerup', onPointerUp);
  document.addEventListener('pointercancel', onPointerUp);
});
